import type React from "react";
import { createContext, useContext, useMemo } from "react";

import { computeMyActiveFeeds, type MyActiveFeedRow, myActiveFeedsCollections } from "@/models/repositories";
import { useCollectionsCompute } from "@/store/useCollectionsCompute";
import { WorkspaceContext } from "./StateProviders/workspaceProvider";

type LiveQueries = {
  myActiveFeeds?: MyActiveFeedRow[];
  myActiveFeedsFiltered?: MyActiveFeedRow[];
  myActiveWriteFeeds?: MyActiveFeedRow[];
  myActiveWriteFeedsFiltered?: MyActiveFeedRow[];
  workspaceMembershipId?: string | null;
};

export const LiveQueryContext = createContext<LiveQueries>({});
type Props = {
  children: React.ReactNode | React.ReactNode[];
};
const LiveQueriesProvider = ({ children }: Props) => {
  const { currentWorkspaceId: workspaceId, myCurrentWorkspaceMembership } = useContext(WorkspaceContext);

  const myMembershipId = myCurrentWorkspaceMembership?.id;

  // List of active feeds that the current user has permissions to
  // Single compute that includes alias counts for all feeds
  const allActiveFeeds = useCollectionsCompute(
    myActiveFeedsCollections,
    () =>
      workspaceId && myMembershipId
        ? computeMyActiveFeeds({
            workspaceId,
            myCurrentWorkspaceMembershipId: myMembershipId,
            filterCommandAlias: false,
          })
        : [],
    [workspaceId, myMembershipId],
    "LiveQueriesProvider:myActiveFeeds",
  );

  const myActiveWriteFeeds = useCollectionsCompute(
    myActiveFeedsCollections,
    () =>
      workspaceId && myMembershipId
        ? computeMyActiveFeeds({
            workspaceId,
            myCurrentWorkspaceMembershipId: myMembershipId,
            filterCommandAlias: false,
            permissionName: "write",
          })
        : [],
    [workspaceId, myMembershipId],
    "LiveQueriesProvider:myActiveWriteFeeds",
  );

  // Derive both datasets from the single query result
  const myActiveFeeds = allActiveFeeds;
  const myActiveFeedsFiltered = useMemo(
    () =>
      allActiveFeeds.filter((feed) => {
        const count = feed.aliasCount as number | null | undefined;
        return (count ?? 0) > 0;
      }),
    [allActiveFeeds],
  );

  const myActiveWriteFeedsFiltered = useMemo(
    () =>
      myActiveWriteFeeds.filter((feed) => {
        const count = feed.aliasCount as number | null | undefined;
        return (count ?? 0) > 0;
      }),
    [myActiveWriteFeeds],
  );

  const LiveQueryState: LiveQueries = {
    myActiveFeeds,
    myActiveFeedsFiltered,
    myActiveWriteFeedsFiltered,
    myActiveWriteFeeds,
    workspaceMembershipId: myCurrentWorkspaceMembership?.id,
  };

  return <LiveQueryContext.Provider value={LiveQueryState}>{children}</LiveQueryContext.Provider>;
};

export default LiveQueriesProvider;
